import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, ShieldCheck } from "lucide-react";
import { createDiscount } from "@/lib/api";
import { CATEGORY_LIST, US_STATES, BRANCHES } from "@/lib/categories";

const initial = {
    business_name: "",
    title: "",
    description: "",
    category: "restaurants",
    discount_details: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    latitude: "",
    longitude: "",
    phone: "",
    website: "",
    is_freebie: false,
    verification_required: true,
    branches: [],
    submitted_by_name: "",
    submitted_by_email: "",
};

export default function Submit() {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const toggleBranch = (b) => {
        setForm((f) => ({
            ...f,
            branches: f.branches.includes(b) ? f.branches.filter((x) => x !== b) : [...f.branches, b],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.business_name || !form.title || !form.description || !form.discount_details || !form.city || !form.state) {
            toast.error("Fill in all required fields.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                latitude: form.latitude ? parseFloat(form.latitude) : null,
                longitude: form.longitude ? parseFloat(form.longitude) : null,
            };
            await createDiscount(payload);
            toast.success("Intel submitted. Pending review.");
            setForm(initial);
            setTimeout(() => navigate("/"), 1200);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const label = "mono-label mb-1 block";
    const inputCls = "w-full bg-[#0e1319] border border-white/15 focus:border-volt outline-none px-3 py-2 text-sm text-white placeholder:text-[#64748b]";

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
            <div className="mono-label text-volt mb-2">// FIELD REPORT</div>
            <h1 className="font-oswald uppercase tracking-wide text-3xl md:text-4xl mb-2">
                Submit a Discount
            </h1>
            <p className="text-[#94a3b8] mb-8 max-w-2xl">
                Spotted a military discount that isn&apos;t here? Submit the intel. Our team verifies and publishes within 48 hours.
            </p>

            <form onSubmit={submit} data-testid="submit-form" className="space-y-6">
                <section className="tactical-card p-6 space-y-4">
                    <div className="mono-label text-volt">// BUSINESS</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className={label}>Business Name *</label>
                            <input
                                data-testid="input-business-name"
                                className={inputCls}
                                value={form.business_name}
                                onChange={(e) => set("business_name", e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className={label}>Website</label>
                            <input
                                data-testid="input-website"
                                className={inputCls}
                                value={form.website}
                                onChange={(e) => set("website", e.target.value)}
                                placeholder="https://"
                            />
                        </div>
                        <div>
                            <label className={label}>Category *</label>
                            <select
                                data-testid="input-category"
                                className={inputCls}
                                value={form.category}
                                onChange={(e) => set("category", e.target.value)}
                            >
                                {CATEGORY_LIST.map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={label}>Phone</label>
                            <input
                                data-testid="input-phone"
                                className={inputCls}
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                <section className="tactical-card p-6 space-y-4">
                    <div className="mono-label text-volt">// DISCOUNT DETAILS</div>
                    <div>
                        <label className={label}>Headline *</label>
                        <input
                            data-testid="input-title"
                            className={inputCls}
                            value={form.title}
                            onChange={(e) => set("title", e.target.value)}
                            placeholder="e.g. 15% Off Every Day"
                            required
                        />
                    </div>
                    <div>
                        <label className={label}>Description *</label>
                        <textarea
                            data-testid="input-description"
                            className={inputCls + " min-h-[80px]"}
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className={label}>Redemption Details *</label>
                        <textarea
                            data-testid="input-details"
                            className={inputCls + " min-h-[80px]"}
                            value={form.discount_details}
                            onChange={(e) => set("discount_details", e.target.value)}
                            placeholder="What exactly does the customer get? Show ID? Promo code?"
                            required
                        />
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                data-testid="input-freebie"
                                checked={form.is_freebie}
                                onChange={(e) => set("is_freebie", e.target.checked)}
                                className="accent-volt"
                            />
                            <span>This is a freebie / no purchase required</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                data-testid="input-verify"
                                checked={form.verification_required}
                                onChange={(e) => set("verification_required", e.target.checked)}
                                className="accent-volt"
                            />
                            <span>Military ID verification required</span>
                        </label>
                    </div>
                    <div>
                        <div className={label}>Eligible Branches</div>
                        <div className="flex flex-wrap gap-2">
                            {BRANCHES.map((b) => (
                                <button
                                    type="button"
                                    key={b}
                                    data-testid={`branch-${b}`}
                                    onClick={() => toggleBranch(b)}
                                    className={`px-3 py-1 border text-xs font-oswald uppercase tracking-wider ${
                                        form.branches.includes(b)
                                            ? "bg-volt text-black border-volt"
                                            : "border-white/15 text-[#94a3b8]"
                                    }`}
                                >
                                    {b}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="tactical-card p-6 space-y-4">
                    <div className="mono-label text-volt">// LOCATION</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className={label}>Street Address</label>
                            <input
                                data-testid="input-address"
                                className={inputCls}
                                value={form.address}
                                onChange={(e) => set("address", e.target.value)}
                            />
                        </div>
                        <div>
                            <label className={label}>City *</label>
                            <input
                                data-testid="input-city"
                                className={inputCls}
                                value={form.city}
                                onChange={(e) => set("city", e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className={label}>State *</label>
                            <select
                                data-testid="input-state"
                                className={inputCls}
                                value={form.state}
                                onChange={(e) => set("state", e.target.value)}
                                required
                            >
                                <option value="">--</option>
                                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className={label}>ZIP</label>
                            <input
                                data-testid="input-zip"
                                className={inputCls}
                                value={form.zip_code}
                                onChange={(e) => set("zip_code", e.target.value)}
                            />
                        </div>
                        <div>
                            <label className={label}>Latitude</label>
                            <input
                                data-testid="input-lat"
                                type="number"
                                step="any"
                                className={inputCls}
                                value={form.latitude}
                                onChange={(e) => set("latitude", e.target.value)}
                            />
                        </div>
                        <div>
                            <label className={label}>Longitude</label>
                            <input
                                data-testid="input-lng"
                                type="number"
                                step="any"
                                className={inputCls}
                                value={form.longitude}
                                onChange={(e) => set("longitude", e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                <section className="tactical-card p-6 space-y-4">
                    <div className="mono-label text-volt">// SUBMITTER (OPTIONAL)</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className={label}>Name</label>
                            <input
                                data-testid="input-submitter-name"
                                className={inputCls}
                                value={form.submitted_by_name}
                                onChange={(e) => set("submitted_by_name", e.target.value)}
                            />
                        </div>
                        <div>
                            <label className={label}>Email</label>
                            <input
                                data-testid="input-submitter-email"
                                className={inputCls}
                                type="email"
                                value={form.submitted_by_email}
                                onChange={(e) => set("submitted_by_email", e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        type="submit"
                        data-testid="submit-btn"
                        disabled={submitting}
                        className="btn-volt disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send className="w-4 h-4" /> {submitting ? "Transmitting..." : "Transmit Intel"}
                    </button>
                    <div className="mono-label text-[10px] text-[#64748b] flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3 text-volt" /> Reviewed within 48h
                    </div>
                </div>
            </form>
        </div>
    );
}
